<template>
  <ClientOverviewCard :title="$t('Client details')" :loading="loading" :loading-tiles="0">
    <WdsStack direction-column gap-md data-test="client-details-card">
      <div v-if="company" class="tw-grid tw-grid-cols-3 tw-gap-4">
        <template v-if="agencyLink">
          <WdsText class="tw-font-large">
            <span class="tw-text-gray-900">{{ $t('Relation') }}</span>
          </WdsText>
          <div class="tw-col-span-2" data-test="client-details-relation">
            <StaffingAgencyClientRelationLabel
              :is-direct="agencyLink.isDirect"
              :managing-company="agencyLink.managingCompany"
            />
          </div>
        </template>
        <!-- Staffing agencies never read a client's address. -->
        <template v-else>
          <WdsText class="tw-font-large">
            <span class="tw-text-gray-900">{{ $t('Address') }}</span>
          </WdsText>
          <div class="tw-col-span-2" data-test="client-details-address">
            <CopyableText v-if="address" :text="address" :icon="WdsIconNameEnum.LOCATION" />
            <WdsText v-else secondary>{{ $t('Not specified') }}</WdsText>
          </div>
        </template>

        <WdsText class="tw-font-large">
          <span class="tw-text-gray-900">{{ $t('Country') }}</span>
        </WdsText>
        <div class="tw-col-span-2">
          <LocationCell
            :country="agencyLink ? (agencyLink.clientCountry?.name ?? '—') : marketName"
            :country-code="agencyLink ? (agencyLink.clientCountry?.isoCode ?? null) : company.market"
            :detail="company.region?.name ?? undefined"
            data-test="client-details-market"
          />
        </div>

        <template v-if="registrationNumber">
          <WdsText class="tw-font-large">
            <span class="tw-text-gray-900">{{ $t('Registration number') }}</span>
          </WdsText>
          <div class="tw-col-span-2" data-test="client-details-registration-number">
            <CopyableText :text="registrationNumber" />
          </div>
        </template>

        <template v-if="company.externalIdentifier">
          <WdsText class="tw-font-large">
            <span class="tw-text-gray-900">{{ $t('External identifier') }}</span>
          </WdsText>
          <div class="tw-col-span-2" data-test="client-details-external-id">
            <CopyableText :text="company.externalIdentifier" />
          </div>
        </template>

        <template v-if="organisations.length > 0">
          <WdsText class="tw-font-large">
            <span class="tw-text-gray-900">{{ $t('Organisations') }}</span>
          </WdsText>
          <WdsStack wrap gap-sm class="tw-col-span-2" data-test="client-details-organisations">
            <WdsLabel
              v-for="organisation in organisations"
              :key="organisation.id"
              :label="organisation.name"
              subtle
              md
            />
          </WdsStack>
        </template>

        <template v-if="links.length > 0">
          <WdsText class="tw-font-large">
            <span class="tw-text-gray-900">{{ $t('Links') }}</span>
          </WdsText>
          <WdsStack wrap gap-sm class="tw-col-span-2 -tw-mx-1" data-test="client-details-links">
            <WdsButtonGhost
              v-for="link in links"
              :key="link.url"
              secondary
              :href="link.url"
              target="_blank"
              rel="noopener"
            >
              <WdsStack align-center gap-xs>
                {{ link.title }}
                <WdsIcon :name="WdsIconNameEnum.LAUNCH" :size="14" />
              </WdsStack>
            </WdsButtonGhost>
          </WdsStack>
        </template>

        <template v-if="description">
          <WdsText class="tw-font-large">
            <span class="tw-text-gray-900">{{ $t('About') }}</span>
          </WdsText>
          <WdsText class="tw-col-span-2 tw-text-gray-900">
            <span
              class="tw-whitespace-pre-line tw-font-normal"
              data-test="client-details-description"
            >
              {{ description }}
            </span>
          </WdsText>
        </template>
      </div>
    </WdsStack>
  </ClientOverviewCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  WdsButtonGhost,
  WdsIcon,
  WdsIconNameEnum,
  WdsLabel,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import CopyableText from '@/components/CopyableText.vue'
import StaffingAgencyClientRelationLabel from '@/components/StaffingAgencyClients/StaffingAgencyClientRelationLabel.vue'
import LocationCell from '@/components/LocationCell.vue'
import ClientOverviewCard from '@/components/Clients/OverviewCards/ClientOverviewCard.vue'

const ClientDetailsCardFields = graphql(`
  fragment ClientDetailsCard on ClientRelation {
    id
    ... on SupplierClient {
      isDirect
      clientCountry {
        isoCode
        name
      }
      managingCompany {
        id
        name
        avatar
      }
    }
    client {
      id
      name
      market
      primaryBusinessEntity {
        companyNumber
      }
      address {
        formattedAddress(multiline: false)
      }
      externalIdentifier
      region {
        id
        name
      }
      profile {
        id
        description
        links {
          title
          url
        }
      }
      organisations(first: 10) {
        data {
          id
          name
        }
      }
    }
  }
`)

const props = defineProps<{
  client: FragmentType<typeof ClientDetailsCardFields> | null
  loading: boolean
}>()

const relation = computed(() => useFragment(ClientDetailsCardFields, props.client ?? null) ?? null)
const company = computed(() => relation.value?.client ?? null)
const agencyLink = computed(() =>
  relation.value && 'managingCompany' in relation.value ? relation.value : null,
)

const address = computed(() => company.value?.address?.formattedAddress ?? null)
const registrationNumber = computed(
  () => company.value?.primaryBusinessEntity?.companyNumber || null,
)
const description = computed(() => company.value?.profile?.description ?? null)
const organisations = computed(() => company.value?.organisations.data ?? [])
const links = computed(() => company.value?.profile?.links ?? [])

// currentMarkets is a page global keyed by lowercase code; read it lazily, never at module scope.
const marketName = computed(() => {
  const code = company.value?.market ?? ''

  return currentMarkets?.[code.toLowerCase()]?.name ?? code
})
</script>
